import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatError, getImageSrc } from "@/lib/api";
import { toast } from "sonner";
import { PlusCircle, Trash2, Package, Wallet, ClipboardList, ShieldCheck, ShieldAlert, Tag, X } from "lucide-react";

const REGIONS = ["Greater Accra", "Ashanti", "Eastern", "Western", "Central", "Volta", "Northern", "Upper East", "Upper West", "Bono", "Bono East", "Ahafo", "Oti", "Savannah", "Western North", "North East"];
const CATEGORIES = ["livestock", "poultry", "agriculture", "fishing"];
const ID_TYPES = [
  { key: "ghana_card", label: "Ghana Card" },
  { key: "voter_id", label: "Voter ID" },
  { key: "drivers_license", label: "Driver's License" },
  { key: "passport", label: "Passport" },
];

export default function SellerDashboard() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState({ active_listings: 0, total_sales: 0, pending_orders: 0, total_orders: 0 });
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", price: "", unit: "kg", category: "agriculture", region: "Greater Accra", stock: "", image_path: "", bulk_tiers: [] });
  const [uploading, setUploading] = useState(false);
  const [showVerify, setShowVerify] = useState(false);
  const [verifyForm, setVerifyForm] = useState({ id_type: "ghana_card", id_number: "", phone: user?.phone || "" });

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/login"); return; }
    if (user.role !== "seller") { nav("/"); return; }
    load();
  }, [user, loading]);

  const load = async () => {
    try {
      const [s, p, o] = await Promise.all([
        api.get("/seller/stats"),
        api.get("/products", { params: { seller_id: user.id } }),
        api.get("/orders/seller"),
      ]);
      setStats(s.data); setProducts(p.data); setOrders(o.data);
    } catch (e) { toast.error(formatError(e)); }
  };

  const uploadImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((s) => ({ ...s, image_path: data.path }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); }
  };

  const createProduct = async (e) => {
    e.preventDefault();
    try {
      const tiers = (form.bulk_tiers || [])
        .filter((t) => t.min_qty && t.price)
        .map((t) => ({ min_qty: parseInt(t.min_qty), price: parseFloat(t.price) }));
      await api.post("/products", {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        bulk_tiers: tiers,
      });
      toast.success("Listing created");
      setShowForm(false);
      setForm({ name: "", description: "", price: "", unit: "kg", category: "agriculture", region: "Greater Accra", stock: "", image_path: "", bulk_tiers: [] });
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const addTier = () => setForm((s) => ({ ...s, bulk_tiers: [...(s.bulk_tiers || []), { min_qty: "", price: "" }] }));
  const updateTier = (i, k, v) => setForm((s) => ({ ...s, bulk_tiers: s.bulk_tiers.map((t, idx) => idx === i ? { ...t, [k]: v } : t) }));
  const removeTier = (i) => setForm((s) => ({ ...s, bulk_tiers: s.bulk_tiers.filter((_, idx) => idx !== i) }));

  const submitVerify = async (e) => {
    e.preventDefault();
    try {
      await api.post("/seller/verify", verifyForm);
      toast.success("You're now a verified seller!");
      const { data } = await api.get("/auth/me");
      setUser(data);
      setShowVerify(false);
    } catch (err) { toast.error(formatError(err)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    await api.delete(`/products/${id}`);
    toast.success("Deleted");
    load();
  };

  if (loading || !user) return <div className="p-10 text-center">Loading...</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex flex-wrap items-start sm:items-end justify-between gap-3 sm:gap-4">
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">Seller Dashboard</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1E4D3B] mt-1 flex flex-wrap items-center gap-2 break-words">
            <span className="break-words">Welcome, {user.name}</span>
            {user.verified && <ShieldCheck className="w-5 h-5 sm:w-6 sm:h-6 text-[#1B804E] shrink-0" title="Verified seller" />}
          </h1>
        </div>
        <button onClick={() => setShowForm(!showForm)} className="btn-primary px-4 sm:px-5 py-2.5 sm:py-3 rounded-xl inline-flex items-center gap-2 text-sm sm:text-base shrink-0" data-testid="seller-new-listing-button">
          <PlusCircle className="w-4 h-4" /> {showForm ? "Cancel" : "New listing"}
        </button>
      </div>

      {!user.verified ? (
        <div className="mt-6 card-farm p-4 sm:p-5 border-l-4 border-[#D99B26] bg-[#FFFBEE]">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0">
              <ShieldAlert className="w-6 h-6 text-[#D99B26] shrink-0 mt-0.5" />
              <div className="min-w-0">
                <div className="font-bold text-[#1E4D3B]">Get your Verified Seller badge</div>
                <div className="text-sm text-[#5C6660]">Buyers place bigger orders with verified farmers. Submit your Ghana Card in under a minute.</div>
              </div>
            </div>
            <button onClick={() => setShowVerify(true)} className="btn-primary px-4 py-2 rounded-lg text-sm shrink-0 self-start sm:self-auto" data-testid="seller-verify-open-button">Verify now</button>
          </div>
        </div>
      ) : (
        <div className="mt-6 card-farm p-4 border-l-4 border-[#1B804E] bg-[#F0FAF3] flex items-center gap-3" data-testid="seller-verified-banner">
          <ShieldCheck className="w-5 h-5 text-[#1B804E] shrink-0" />
          <div className="text-sm text-[#1E4D3B] font-semibold">You are a verified AgriNuhse seller.</div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
        <StatCard icon={Wallet} label="Total sales (GH₵)" value={stats.total_sales.toFixed(2)} testid="stat-total-sales" />
        <StatCard icon={Package} label="Active listings" value={stats.active_listings} testid="stat-active-listings" />
        <StatCard icon={ClipboardList} label="Pending orders" value={stats.pending_orders} testid="stat-pending-orders" />
      </div>

      {showForm && (
        <form onSubmit={createProduct} className="card-farm p-4 sm:p-6 mt-8 space-y-4" data-testid="seller-new-listing-form">
          <h2 className="text-xl font-bold text-[#1E4D3B]">Create new listing</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Product name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" data-testid="listing-name-input" /></Field>
            <Field label="Price (GH₵)"><input required type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="input" data-testid="listing-price-input" /></Field>
            <Field label="Unit"><input required value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })} placeholder="kg, bird, head, crate" className="input" data-testid="listing-unit-input" /></Field>
            <Field label="Stock quantity"><input required type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} className="input" data-testid="listing-stock-input" /></Field>
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="input" data-testid="listing-category-select">
                {CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}
              </select>
            </Field>
            <Field label="Region">
              <select value={form.region} onChange={(e) => setForm({ ...form, region: e.target.value })} className="input" data-testid="listing-region-select">
                {REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Description"><textarea required rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="input" data-testid="listing-description-input" /></Field>

          <div>
            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53] inline-flex items-center gap-1"><Tag className="w-3 h-3" /> Bulk discount tiers (optional)</label>
              <button type="button" onClick={addTier} className="text-xs font-semibold text-[#C85A32]" data-testid="add-tier-button">+ Add tier</button>
            </div>
            {(form.bulk_tiers || []).length === 0 ? (
              <div className="text-xs text-[#8C6D53]">Give restaurants and resellers a break for buying more.</div>
            ) : (
              <div className="space-y-3">
                {form.bulk_tiers.map((t, i) => (
                  <div key={i} className="rounded-lg border border-[#E5D9C8] p-3 bg-[#FDFBF7]" data-testid={`tier-row-${i}`}>
                    <div className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                      <div>
                        <label className="text-[10px] uppercase font-bold tracking-widest text-[#8C6D53]">Buy at least ({form.unit || "units"})</label>
                        <input type="number" value={t.min_qty} onChange={(e) => updateTier(i, "min_qty", e.target.value)} placeholder="10" className="input" data-testid={`tier-minqty-${i}`} />
                      </div>
                      <div>
                        <label className="text-[10px] uppercase font-bold tracking-widest text-[#8C6D53]">Price each (GH₵)</label>
                        <input type="number" step="0.01" value={t.price} onChange={(e) => updateTier(i, "price", e.target.value)} placeholder="45.00" className="input" data-testid={`tier-price-${i}`} />
                      </div>
                      <button type="button" onClick={() => removeTier(i)} className="p-2 text-[#D93838] hover:bg-red-50 rounded-lg self-start sm:self-end justify-self-end sm:justify-self-auto" data-testid={`tier-remove-${i}`} aria-label="Remove tier"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53]">Product photo</label>
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={uploadImage} className="mt-1 block text-sm max-w-full" data-testid="listing-image-input" />
            {uploading && <div className="text-xs text-[#5C6660] mt-1">Uploading...</div>}
            {form.image_path && <div className="text-xs text-[#1B804E] mt-1">✓ Image uploaded</div>}
          </div>
          <button className="btn-primary w-full sm:w-auto px-5 py-3 rounded-xl" data-testid="listing-submit-button">Publish listing</button>
        </form>
      )}

      <div className="mt-10">
        <h2 className="text-xl font-bold text-[#1E4D3B] mb-4">My listings ({products.length})</h2>
        {products.length === 0 ? (
          <div className="card-farm p-8 text-center text-[#5C6660]">No listings yet. Create your first one above.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {products.map((p) => (
              <div key={p.id} className="card-farm p-3 flex gap-3" data-testid={`seller-product-${p.id}`}>
                <img src={getImageSrc(p)} alt={p.name} className="w-20 h-20 object-cover rounded-lg" />
                <div className="flex-1">
                  <div className="font-bold">{p.name}</div>
                  <div className="text-xs text-[#5C6660] capitalize">{p.category} · {p.region}</div>
                  <div className="text-[#C85A32] font-black mt-1">GH₵ {p.price.toFixed(2)}</div>
                  <div className="text-xs text-[#5C6660]">{p.stock} {p.unit} in stock</div>
                </div>
                <button onClick={() => del(p.id)} className="p-2 text-[#D93838] hover:bg-red-50 rounded h-fit" data-testid={`seller-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="mt-10">
        <h2 className="text-xl font-bold text-[#1E4D3B] mb-4">Recent orders ({orders.length})</h2>
        {orders.length === 0 ? (
          <div className="card-farm p-8 text-center text-[#5C6660]">No orders yet.</div>
        ) : (
          <div className="space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="card-farm p-4" data-testid={`seller-order-${o.id}`}>
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <div className="font-mono text-xs text-[#8C6D53]">{o.payment.reference}</div>
                    <div className="font-bold">{o.buyer_name} · {o.delivery_region}</div>
                    <div className="text-xs text-[#5C6660]">{new Date(o.created_at).toLocaleString()}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[#C85A32] font-black text-lg">GH₵ {o.total.toFixed(2)}</div>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#1B804E] text-white uppercase font-bold tracking-widest">{o.status}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`.input { width:100%; padding:.6rem .75rem; border:1px solid #E5D9C8; border-radius:.5rem; background:white; font-size:.875rem; margin-top:.25rem; }`}</style>

      {showVerify && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowVerify(false)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitVerify} className="bg-white rounded-2xl p-6 w-full max-w-md" data-testid="verify-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-[#1E4D3B]">Seller verification</h3>
              <button type="button" onClick={() => setShowVerify(false)} className="p-1"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-[#5C6660]">Submit any government-issued ID. We verify instantly using your registered phone number.</p>
            <div className="mt-4 space-y-3">
              <Field label="ID type">
                <select value={verifyForm.id_type} onChange={(e) => setVerifyForm({ ...verifyForm, id_type: e.target.value })} className="input" data-testid="verify-id-type">
                  {ID_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                </select>
              </Field>
              <Field label="ID number">
                <input required value={verifyForm.id_number} onChange={(e) => setVerifyForm({ ...verifyForm, id_number: e.target.value })} placeholder="GHA-XXXXXXXXX-X" className="input" data-testid="verify-id-number" />
              </Field>
              <Field label="Ghana phone number">
                <input required value={verifyForm.phone} onChange={(e) => setVerifyForm({ ...verifyForm, phone: e.target.value })} placeholder="024XXXXXXX" className="input" data-testid="verify-phone" />
              </Field>
            </div>
            <button className="btn-primary w-full py-3 rounded-xl mt-5" data-testid="verify-submit">Verify me</button>
          </form>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="text-xs uppercase font-bold tracking-widest text-[#8C6D53]">{label}</label>
    {children}
  </div>
);

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="card-farm p-5">
    <div className="flex items-center gap-3">
      <div className="w-10 h-10 rounded-lg bg-[#F3ECE2] flex items-center justify-center text-[#C85A32]"><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-xs uppercase tracking-widest font-bold text-[#8C6D53]">{label}</div>
        <div className="text-2xl font-black text-[#1E4D3B]" data-testid={testid}>{value}</div>
      </div>
    </div>
  </div>
);
